<template>
    <div class="inventory-detail">
        <InventoryDetailSkeleton v-if="loading" />

        <div v-else-if="!detail" class="empty-state">
            <i class="fas fa-file-invoice"></i>
            <p>인벤토리를 선택해주세요</p>
            <small>좌측 목록에서 인벤토리를 선택하면 상세 정보가 표시됩니다.</small>
        </div>

        <div v-else-if="isTotalEmissionZero" class="empty-state">
            <i class="fas fa-chart-line"></i>
            <p>배출량 데이터가 없습니다</p>
            <small>이 인벤토리에는 아직 계산된 배출량이 없습니다.</small>
        </div>

        <div v-else class="detail-container">
            <!-- 헤더: 조직경계 중심, 사용자 친화적 -->
            <div class="detail-header">
                <div class="header-info">
                    <div class="header-primary">
                        <h2 v-if="orgBoundaryName" class="header-title">{{ orgBoundaryName }}</h2>
                        <h2 v-else class="header-title header-title--muted">조직경계 미지정</h2>
                    </div>
                    <div class="header-meta">
                        <span class="header-badge header-period">
                            {{ formatPeriod(detail.period ?? detail.periodFrom)
                            }}{{
                                detail.periodTo && detail.periodTo !== (detail.period ?? detail.periodFrom)
                                    ? " ~ " + formatPeriod(detail.periodTo)
                                    : ""
                            }}
                        </span>
                        <span v-if="operBoundaryName" class="header-badge header-oper">{{ operBoundaryName }}</span>
                        <span class="header-badge header-id">인벤토리 #{{ detail.inventoryIdx }}</span>
                    </div>
                </div>
                <div class="header-actions">
                    <button class="action-btn guide-btn" title="가이드" @click="showGuideModal">
                        <i class="fas fa-question-circle"></i>
                    </button>
                    <CustomDropdown :items="headerActionItems" align="bottom">
                        <template #trigger>
                            <span class="action-btn action-btn--more"><i class="fas fa-ellipsis-v"></i></span>
                        </template>
                    </CustomDropdown>
                </div>
            </div>

            <div class="detail-body">
                <!-- 상단: 메인 메트릭 카드들 -->
                <div class="top-metrics">
                    <!-- 총 배출량 카드 -->
                    <div class="main-metric-card emission-card">
                        <i class="fas fa-tree metric-bg-icon"></i>
                        <div class="main-metric-content">
                            <div class="main-metric-header">
                                <span class="main-metric-label">총 배출량</span>
                                <span class="main-metric-timestamp">{{
                                    formatDateTime(detail.createdAt || detail.updatedAt)
                                }}</span>
                            </div>
                            <div class="main-metric-value-section">
                                <div class="main-metric-value">
                                    <span class="main-metric-number">{{
                                        formatEmission(detail.summary.totalEmission, 1)
                                    }}</span>
                                    <span class="main-metric-unit">kgCO2e</span>
                                </div>
                            </div>
                            <div class="main-metric-stats">
                                <div class="main-stat-item">
                                    <span class="main-stat-label">Scope 1</span>
                                    <span class="main-stat-value">{{
                                        formatEmission(detail.summary.scope1Emission, 1)
                                    }}</span>
                                </div>
                                <div class="main-stat-item">
                                    <span class="main-stat-label">Scope 2</span>
                                    <span class="main-stat-value">{{
                                        formatEmission(detail.summary.scope2Emission, 1)
                                    }}</span>
                                </div>
                                <div class="main-stat-item">
                                    <span class="main-stat-label">Scope 3</span>
                                    <span class="main-stat-value">{{
                                        formatEmission(detail.summary.scope3Emission, 1)
                                    }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 품질 점수 카드 -->
                    <div class="main-metric-card quality-card">
                        <i class="fas fa-clipboard-check metric-bg-icon"></i>
                        <div class="main-metric-content">
                            <div class="main-metric-header">
                                <span class="main-metric-label">품질 점수</span>
                                <button v-if="qualityResult" class="main-metric-btn" @click="showQualityDetail">
                                    <i class="material-icons">open_in_new</i>
                                </button>
                            </div>
                            <div v-if="qualityLoading" class="main-metric-loading">계산중...</div>
                            <div v-else-if="!qualityResult" class="main-metric-empty">
                                <button class="main-metric-action-btn" @click="requestQualityCheck">점검 실행</button>
                            </div>
                            <div v-else class="main-metric-result">
                                <div class="main-metric-value-section">
                                    <div class="main-metric-score" :class="qualityStatusClass">{{ qualityScore }}</div>
                                    <div class="main-metric-status" :class="qualityStatusClass">
                                        {{ qualityStatusLabel }}
                                    </div>
                                </div>
                                <div class="main-metric-stats">
                                    <div class="main-stat-item">
                                        <span class="main-stat-label">통과</span>
                                        <span class="main-stat-value stat-success">{{ qualitySummary.passed }}</span>
                                    </div>
                                    <div class="main-stat-item">
                                        <span class="main-stat-label">경고</span>
                                        <span class="main-stat-value stat-warning">{{ qualitySummary.warnings }}</span>
                                    </div>
                                    <div class="main-stat-item">
                                        <span class="main-stat-label">오류</span>
                                        <span class="main-stat-value stat-error">{{ qualitySummary.errors }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 불확도 카드 -->
                    <div class="main-metric-card uncertainty-card">
                        <i class="material-icons metric-bg-icon">blur_on</i>
                        <div class="main-metric-content">
                            <div class="main-metric-header">
                                <span class="main-metric-label">불확도</span>
                                <button
                                    v-if="uncertaintyResult"
                                    class="main-metric-btn"
                                    @click="requestUncertaintyCheck"
                                >
                                    <i class="material-icons">open_in_new</i>
                                </button>
                            </div>
                            <div v-if="uncertaintyLoading" class="main-metric-loading">계산중...</div>
                            <div v-else-if="!uncertaintyResult" class="main-metric-empty">
                                <button class="main-metric-action-btn" @click="requestUncertaintyCheck">
                                    분석 실행
                                </button>
                            </div>
                            <div v-else class="main-metric-result">
                                <div class="main-metric-value-section">
                                    <div class="main-metric-score" :class="`level-${uncertaintyLevelKey}`">
                                        {{ formatPercent(uncertaintyResult.uncertainty?.percentage) }}
                                    </div>
                                    <div class="main-metric-status" :class="`level-${uncertaintyLevelKey}`">
                                        {{ uncertaintyLevelLabel }}
                                    </div>
                                </div>
                                <div class="main-metric-stats">
                                    <div class="main-stat-item">
                                        <span class="main-stat-label">신뢰도</span>
                                        <span class="main-stat-value">{{ uncertaintyInterval.confidence }}%</span>
                                    </div>
                                    <div class="main-stat-item">
                                        <span class="main-stat-label">상한</span>
                                        <span class="main-stat-value">{{
                                            formatEmission(uncertaintyInterval.upper, 1)
                                        }}</span>
                                    </div>
                                    <div class="main-stat-item">
                                        <span class="main-stat-label">하한</span>
                                        <span class="main-stat-value">{{
                                            formatEmission(uncertaintyInterval.lower, 1)
                                        }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 하단: 산정내역(1순위) → 카테고리·시설(동일 위계). detail-body 스크롤 없음 — 미리보기 행 수 제한 + 전체는 모달 -->
                <div class="lower-data">
                    <section class="inv-panel inv-panel--primary">
                        <header class="inv-panel-head">
                            <div class="inv-panel-head-text">
                                <h3 class="inv-panel-title">산정내역</h3>
                                <span class="inv-panel-meta">{{ detail.lines?.length || 0 }}건</span>
                            </div>
                        </header>
                        <div class="inv-panel-body">
                            <div v-if="linesPreview.length" class="lines-preview-wrap">
                                <table class="lines-preview-table">
                                    <thead>
                                        <tr>
                                            <th scope="col" class="lines-preview-th lines-preview-th--scope">Scope</th>
                                            <th scope="col" class="lines-preview-th lines-preview-th--main">
                                                시설 · 카테고리
                                            </th>
                                            <th
                                                scope="col"
                                                class="lines-preview-th lines-preview-th--num lines-preview-th--emission"
                                            >
                                                배출량(kgCO2e)
                                            </th>
                                            <th
                                                scope="col"
                                                class="lines-preview-th lines-preview-th--num lines-preview-th--gas"
                                            >
                                                CO2
                                            </th>
                                            <th
                                                scope="col"
                                                class="lines-preview-th lines-preview-th--num lines-preview-th--gas"
                                            >
                                                CH4
                                            </th>
                                            <th
                                                scope="col"
                                                class="lines-preview-th lines-preview-th--num lines-preview-th--gas"
                                            >
                                                N2O
                                            </th>
                                            <th
                                                scope="col"
                                                class="lines-preview-th lines-preview-th--num lines-preview-th--gas"
                                            >
                                                HFCs
                                            </th>
                                            <th
                                                scope="col"
                                                class="lines-preview-th lines-preview-th--num lines-preview-th--gas"
                                            >
                                                PFCs
                                            </th>
                                            <th
                                                scope="col"
                                                class="lines-preview-th lines-preview-th--num lines-preview-th--gas"
                                            >
                                                SF6
                                            </th>
                                            <th
                                                scope="col"
                                                class="lines-preview-th lines-preview-th--num lines-preview-th--activity"
                                            >
                                                계산
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="(line, index) in linesPreview"
                                            :key="line.lineIdx || index"
                                            class="lines-preview-row"
                                            tabindex="0"
                                            role="button"
                                            @click="openLineDetailModal(line)"
                                            @keydown.enter.prevent="openLineDetailModal(line)"
                                            @keydown.space.prevent="openLineDetailModal(line)"
                                        >
                                            <td class="lines-preview-td">
                                                <span class="scope-pill">S{{ line.scope || 1 }}</span>
                                            </td>
                                            <td class="lines-preview-td lines-preview-td--main">
                                                <span class="lines-preview-facility">{{
                                                    line.facilityName || getFacilityName(line) || "—"
                                                }}</span>
                                                <span class="lines-preview-sep">·</span>
                                                <span class="lines-preview-cat">{{
                                                    line.categoryName || line.categoryCode || "—"
                                                }}</span>
                                            </td>
                                            <td
                                                class="lines-preview-td lines-preview-td--num lines-preview-td--emission"
                                            >
                                                {{ formatEmission(getLineEmission(line), 1) }}
                                            </td>
                                            <td class="lines-preview-td lines-preview-td--num">
                                                {{ formatEmission(getLineGasCo2eq(line, "CO2"), 2) }}
                                            </td>
                                            <td class="lines-preview-td lines-preview-td--num">
                                                {{ formatEmission(getLineGasCo2eq(line, "CH4"), 2) }}
                                            </td>
                                            <td class="lines-preview-td lines-preview-td--num">
                                                {{ formatEmission(getLineGasCo2eq(line, "N2O"), 2) }}
                                            </td>
                                            <td class="lines-preview-td lines-preview-td--num">
                                                {{ formatEmission(getLineGasCo2eq(line, "HFCs"), 2) }}
                                            </td>
                                            <td class="lines-preview-td lines-preview-td--num">
                                                {{ formatEmission(getLineGasCo2eq(line, "PFCs"), 2) }}
                                            </td>
                                            <td class="lines-preview-td lines-preview-td--num">
                                                {{ formatEmission(getLineGasCo2eq(line, "SF6"), 2) }}
                                            </td>
                                            <td
                                                class="lines-preview-td lines-preview-td--num lines-preview-td--activity"
                                            >
                                                {{ line.calcLineCount }}건
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                                <p
                                    v-if="(detail.lines?.length || 0) > linesPreview.length"
                                    class="lines-preview-footnote"
                                >
                                    미리보기 {{ linesPreview.length }}건 · 나머지는 「전체 보기」
                                </p>
                            </div>
                            <div v-else class="content-empty">
                                <span>산정내역 데이터가 없습니다.</span>
                            </div>
                        </div>
                    </section>

                    <div class="inv-panel-row">
                        <section class="inv-panel inv-panel--secondary">
                            <header class="inv-panel-head">
                                <div class="inv-panel-head-text">
                                    <h3 class="inv-panel-title">카테고리별 배출량</h3>
                                    <span class="inv-panel-meta">{{ detail.byCategory?.length || 0 }}개</span>
                                </div>
                                <button
                                    type="button"
                                    class="inv-panel-icon-btn"
                                    title="전체"
                                    @click="showCategoriesModal"
                                >
                                    <i class="material-icons">open_in_new</i>
                                </button>
                            </header>
                            <div class="inv-panel-body">
                                <div v-if="categoryPreview.length" class="inv-mini-list">
                                    <div v-for="(category, index) in categoryPreview" :key="index" class="inv-mini-row">
                                        <span class="scope-pill">S{{ getScopeNumber(category.scopeType) }}</span>
                                        <span
                                            class="inv-mini-name"
                                            :title="category.categoryName || category.categoryCode"
                                            >{{ category.categoryName || category.categoryCode || "—" }}</span
                                        >
                                        <span class="inv-mini-val">
                                            {{ formatEmission(category.emission, 1) }}
                                            <span class="inv-mini-unit">kgCO2e</span>
                                        </span>
                                        <span class="inv-mini-pct"
                                            >{{
                                                calculatePercentage(category.emission, detail.summary.totalEmission)
                                            }}%</span
                                        >
                                        <div class="inv-mini-bar" aria-hidden="true">
                                            <div
                                                class="inv-mini-bar-fill"
                                                :style="{
                                                    width:
                                                        calculatePercentage(
                                                            category.emission,
                                                            detail.summary.totalEmission,
                                                        ) + '%',
                                                }"
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="content-empty">
                                    <span>카테고리별 데이터가 없습니다.</span>
                                </div>
                            </div>
                        </section>

                        <section class="inv-panel inv-panel--secondary">
                            <header class="inv-panel-head">
                                <div class="inv-panel-head-text">
                                    <h3 class="inv-panel-title">시설별 배출량</h3>
                                    <span class="inv-panel-meta">{{ detail.byFacility?.length || 0 }}개</span>
                                </div>
                                <button
                                    type="button"
                                    class="inv-panel-icon-btn"
                                    title="전체"
                                    @click="showFacilitiesModal"
                                >
                                    <i class="material-icons">open_in_new</i>
                                </button>
                            </header>
                            <div class="inv-panel-body">
                                <div v-if="facilityPreview.length" class="inv-mini-list">
                                    <div
                                        v-for="(facility, index) in facilityPreview"
                                        :key="index"
                                        class="inv-mini-row inv-mini-row--facility"
                                    >
                                        <span class="inv-mini-rank">{{ index + 1 }}</span>
                                        <span
                                            class="inv-mini-name"
                                            :title="facility.facilityName || getFacilityName(facility)"
                                            >{{ facility.facilityName || getFacilityName(facility) || "—" }}</span
                                        >
                                        <span class="inv-mini-val">
                                            {{ formatEmission(facility.totalEmission, 1) }}
                                            <span class="inv-mini-unit">kgCO2e</span>
                                        </span>
                                        <span class="inv-mini-pct"
                                            >{{
                                                calculatePercentage(
                                                    facility.totalEmission,
                                                    detail.summary.totalEmission,
                                                )
                                            }}%</span
                                        >
                                    </div>
                                </div>
                                <div v-else class="content-empty">
                                    <span>시설별 데이터가 없습니다.</span>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            <!-- 산정내역 모달 (라인 클릭 시 단일 화면 상세) -->
            <b-modal
                v-model="showLinesModalFlag"
                size="xl"
                dialog-class="line-detail-modal-dialog"
                centered
                hide-header
                ok-only
                ok-title="닫기"
                @hidden="closeLinesModal"
            >
                <div v-if="selectedLineDetail" class="line-detail-modal-shell">
                    <div class="line-detail-modal-header">
                        <div class="line-detail-header-main">
                            <span class="line-detail-header-title">산정내역 상세</span>
                            <span class="line-detail-header-sep">|</span>
                            <span class="line-detail-header-sub">{{ lineDetailFacilityDisplay }}</span>
                            <span class="line-detail-header-sep">|</span>
                            <span class="line-detail-header-cat">{{ lineDetailCategoryDisplay }}</span>
                        </div>
                        <button class="line-detail-header-close" type="button" @click="closeLinesModal">
                            <i class="material-icons">close</i>
                        </button>
                    </div>

                    <div class="line-detail-modal-scroll line-detail-modal-scroll--dense">
                        <div class="line-detail-grid line-detail-grid--summary">
                            <section class="line-detail-summary-card line-detail-summary-card--hero">
                                <div class="line-detail-summary-main">
                                    <span class="line-detail-summary-label">총 배출량 (CO2eq)</span>
                                    <div class="line-detail-summary-value-wrap">
                                        <strong class="line-detail-summary-value">{{
                                            formatLineModalEmission(lineCo2eqTotalDisplay, 4)
                                        }}</strong>
                                        <span v-if="lineCo2eqTotalDisplay != null" class="line-detail-summary-unit">
                                            kgCO2e
                                        </span>
                                    </div>
                                </div>
                                <div class="line-detail-summary-stats">
                                    <div class="line-detail-summary-stat">
                                        <span class="line-detail-summary-stat-k">질량 합계</span>
                                        <span class="line-detail-summary-stat-v"
                                            >{{ formatLineModalEmission(lineRawMassSumKg, 4)
                                            }}<span v-if="lineRawMassSumKg != null">kg</span></span
                                        >
                                    </div>
                                    <div class="line-detail-summary-stat">
                                        <span class="line-detail-summary-stat-k">계산 건수</span>
                                        <span class="line-detail-summary-stat-v"
                                            >{{
                                                selectedLineDetail.calcLineCount != null
                                                    ? selectedLineDetail.calcLineCount
                                                    : ""
                                            }}{{ selectedLineDetail.calcLineCount != null ? "건" : "" }}</span
                                        >
                                    </div>
                                </div>
                            </section>

                            <section class="line-detail-meta-grid-card">
                                <div class="line-detail-meta-grid-cell">
                                    <span class="line-detail-meta-l">기간</span>
                                    <span class="line-detail-meta-v">{{
                                        formatPeriod(selectedLineDetail.period)
                                    }}</span>
                                </div>
                                <div class="line-detail-meta-grid-cell">
                                    <span class="line-detail-meta-l">카테고리 유형</span>
                                    <span class="line-detail-meta-v">{{
                                        selectedLineDetail.categoryScopeType || ""
                                    }}</span>
                                </div>
                                <div class="line-detail-meta-grid-cell">
                                    <span class="line-detail-meta-l">시설</span>
                                    <span class="line-detail-meta-v">{{ lineDetailFacilityDisplay }}</span>
                                </div>
                                <div class="line-detail-meta-grid-cell">
                                    <span class="line-detail-meta-l">카테고리</span>
                                    <span class="line-detail-meta-v">{{ lineDetailCategoryDisplay }}</span>
                                </div>
                            </section>
                        </div>

                        <section class="line-detail-section line-detail-section--factor">
                            <div class="line-detail-grid line-detail-grid--factor">
                                <div class="line-detail-factor-col">
                                    <h5 class="line-detail-section-title line-detail-section-title--compact">
                                        GWP 프로필
                                    </h5>
                                    <div v-if="lineDetailApiLoading" class="line-detail-api-status">불러오는 중…</div>
                                    <div
                                        v-else-if="lineDetailApiError"
                                        class="line-detail-api-status line-detail-api-status--error"
                                    >
                                        {{ lineDetailApiError }}
                                    </div>
                                    <template v-else-if="inventoryLineDetailPayload">
                                        <p
                                            v-if="inventoryLineDetailPayload.traceUnavailableReason"
                                            class="line-detail-trace-note"
                                        >
                                            {{ lineDetailTraceUnavailableMessage }}
                                        </p>
                                        <div v-if="inventoryLineDetailPayload.gwp" class="line-factor-meta-table-wrap">
                                            <table
                                                class="line-factor-meta-table line-factor-meta-table--gwp line-factor-meta-table--horizontal"
                                            >
                                                <thead>
                                                    <tr>
                                                        <th scope="col">GWP 프로필</th>
                                                        <th scope="col">버전</th>
                                                        <th scope="col">기간지평(년)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>{{ inventoryLineDetailPayload.gwp.profileCode || "" }}</td>
                                                        <td>{{ inventoryLineDetailPayload.gwp.gwpVersion || "" }}</td>
                                                        <td class="line-factor-meta-value--num">
                                                            {{
                                                                inventoryLineDetailPayload.gwp.horizon != null
                                                                    ? inventoryLineDetailPayload.gwp.horizon
                                                                    : ""
                                                            }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div
                                            v-if="inventoryLineDetailPayload.gwpValues?.length"
                                            class="line-factor-table-wrap"
                                        >
                                            <table class="line-factor-table">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">가스</th>
                                                        <th scope="col">GWP</th>
                                                        <th scope="col">근거</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr
                                                        v-for="(gv, gvi) in inventoryLineDetailPayload.gwpValues"
                                                        :key="'gwpv-' + (gv.gwpValueIdx ?? gvi)"
                                                    >
                                                        <td>{{ gv.gasCode || "" }}</td>
                                                        <td class="text-right line-factor-num">
                                                            {{ formatLineModalEmission(gv.gwpValue, 4) }}
                                                        </td>
                                                        <td class="line-factor-remarks">{{ gv.sourceRef || "" }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </template>
                                </div>

                                <div class="line-detail-factor-col">
                                    <h5 class="line-detail-section-title line-detail-section-title--compact">
                                        배출계수
                                    </h5>
                                    <template
                                        v-if="
                                            !lineDetailApiLoading && !lineDetailApiError && inventoryLineDetailPayload
                                        "
                                    >
                                        <div v-if="lineDetailPrimaryFactorSummary" class="line-factor-primary">
                                            <table
                                                class="line-factor-meta-table line-factor-meta-table--factor line-factor-meta-table--horizontal"
                                            >
                                                <thead>
                                                    <tr>
                                                        <th scope="col">계수</th>
                                                        <th scope="col">코드</th>
                                                        <th scope="col">유형</th>
                                                        <th scope="col">가스</th>
                                                        <th scope="col">CO2eq</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>
                                                            {{
                                                                lineDetailPrimaryFactorSummary.emissionFactor
                                                                    ?.factorName ||
                                                                lineDetailPrimaryFactorSummary.emissionFactor
                                                                    ?.factorCode ||
                                                                ""
                                                            }}
                                                        </td>
                                                        <td>
                                                            {{
                                                                lineDetailPrimaryFactorSummary.emissionFactor
                                                                    ?.factorCode || ""
                                                            }}
                                                        </td>
                                                        <td>
                                                            {{
                                                                lineDetailPrimaryFactorSummary.emissionFactor
                                                                    ?.factorType || ""
                                                            }}
                                                        </td>
                                                        <td>{{ lineDetailPrimaryFactorSummary.gasCode || "" }}</td>
                                                        <td class="line-factor-meta-value--emph">
                                                            {{
                                                                formatLineModalEmission(
                                                                    lineDetailPrimaryFactorSummary.co2eqContribution,
                                                                    6,
                                                                )
                                                            }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div
                                            v-if="inventoryLineDetailPayload.factorComponents?.length"
                                            class="line-factor-table-wrap line-factor-table-wrap--components"
                                        >
                                            <table class="line-factor-table line-factor-table--components">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">구성 코드</th>
                                                        <th scope="col">집계 가스</th>
                                                        <th scope="col">배출계수</th>
                                                        <th scope="col">단위</th>
                                                        <th scope="col">비고</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr
                                                        v-for="(fc, fci) in inventoryLineDetailPayload.factorComponents"
                                                        :key="'fc-' + (fc.componentIdx ?? fci)"
                                                    >
                                                        <td>{{ fc.componentCode || "" }}</td>
                                                        <td>{{ fc.aggrGasCode || "" }}</td>
                                                        <td class="text-right line-factor-num">
                                                            {{ formatLineModalEmission(fc.emissionFactor, 8) }}
                                                        </td>
                                                        <td>{{ fc.emissionUnit || "" }}</td>
                                                        <td class="line-factor-remarks">{{ fc.remarks || "" }}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </section>

                        <div class="line-detail-grid line-detail-grid--bottom">
                            <section class="line-detail-section line-detail-section--gas">
                                <h5 class="line-detail-section-title line-detail-section-title--compact">
                                    가스별 내역
                                </h5>
                                <div class="line-detail-gas-panel">
                                    <div class="line-gas-horizontal-wrap">
                                        <table class="line-gas-table line-gas-table--horizontal">
                                            <thead>
                                                <tr>
                                                    <th class="line-gas-corner" scope="col">구분</th>
                                                    <th
                                                        v-for="row in lineGasDetailRows"
                                                        :key="'gh-' + row.gasKey"
                                                        class="line-gas-col-gas"
                                                        scope="col"
                                                    >
                                                        {{ row.gasLabel }}
                                                    </th>
                                                    <th class="line-gas-col-total" scope="col">합계</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <th scope="row" class="line-gas-row-label">질량 (kg)</th>
                                                    <td
                                                        v-for="row in lineGasDetailRows"
                                                        :key="'gm-' + row.gasKey"
                                                        class="line-gas-num text-right"
                                                    >
                                                        {{ formatLineModalEmission(row.massKg, 4) }}
                                                    </td>
                                                    <td class="line-gas-num text-right line-gas-col-total">
                                                        {{ formatLineModalEmission(lineRawMassSumKg, 4) }}
                                                    </td>
                                                </tr>
                                                <tr class="line-gas-row--co2eq">
                                                    <th scope="row" class="line-gas-row-label">CO2eq (kgCO2e)</th>
                                                    <td
                                                        v-for="row in lineGasDetailRows"
                                                        :key="'ge-' + row.gasKey"
                                                        class="line-gas-num line-gas-num--eq text-right"
                                                    >
                                                        {{ formatLineModalEmission(row.co2eqKg, 4) }}
                                                    </td>
                                                    <td
                                                        class="line-gas-num line-gas-num--eq text-right line-gas-col-total"
                                                    >
                                                        {{ formatLineModalEmission(lineCo2eqTotalDisplay, 4) }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </section>

                            <section
                                v-if="selectedLineDetail.calculationFormula || selectedLineDetail.calculationDetails"
                                class="line-detail-section line-detail-section--calc line-detail-section--calc-side"
                            >
                                <h5 class="line-detail-section-title line-detail-section-title--compact">
                                    계산식 요약
                                </h5>
                                <div
                                    v-if="selectedLineDetail.calculationDetails"
                                    class="line-detail-calc-card"
                                    role="button"
                                    tabindex="0"
                                    aria-label="계산 상세 열기"
                                    @click="showLineCalcDetailsModal = true"
                                    @keydown.enter.prevent="showLineCalcDetailsModal = true"
                                    @keydown.space.prevent="showLineCalcDetailsModal = true"
                                >
                                    <div
                                        v-if="selectedLineDetail.calculationFormula"
                                        class="line-detail-calc-card-body line-detail-calc-card-body--row"
                                    >
                                        <div class="line-detail-calc-card-main">
                                            <code class="formula-code-compact line-detail-calc-code">{{
                                                selectedLineDetail.calculationFormula
                                            }}</code>
                                        </div>
                                        <i class="material-icons line-detail-calc-card-chevron" aria-hidden="true"
                                            >chevron_right</i
                                        >
                                    </div>
                                    <div
                                        v-else
                                        class="line-detail-calc-card-body line-detail-calc-card-body--empty line-detail-calc-card-body--row"
                                    >
                                        <span class="line-detail-calc-empty-text">계산 상세 데이터가 있습니다.</span>
                                        <i class="material-icons line-detail-calc-card-chevron" aria-hidden="true"
                                            >chevron_right</i
                                        >
                                    </div>
                                </div>
                                <div v-else-if="selectedLineDetail.calculationFormula" class="line-detail-formula">
                                    <code class="formula-code-compact">{{
                                        selectedLineDetail.calculationFormula
                                    }}</code>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </b-modal>

            <!-- 산정 라인 계산 상세 (JSON) — 보조 모달 (메인보다 위에 뜨도록 별도) -->
            <b-modal
                v-model="showLineCalcDetailsModal"
                title="계산 상세"
                size="lg"
                centered
                hide-footer
                body-class="line-calc-json-modal-body"
                @hidden="showLineCalcDetailsModal = false"
            >
                <pre v-if="selectedLineDetail" class="json-display json-display--line-calc">{{
                    formattedCalculationDetails
                }}</pre>
            </b-modal>

            <!-- 카테고리별 모달 -->
            <b-modal
                v-model="showCategoriesModalFlag"
                title="카테고리별 배출량"
                size="xl"
                centered
                hide-footer
                @hidden="closeCategoriesModal"
            >
                <CommonTable width="100%" margin="0" maxHeight="60vh">
                    <template #header-cells>
                        <th>카테고리</th>
                        <th>카테고리 코드</th>
                        <th>Scope</th>
                        <th class="text-right">계산 건수</th>
                        <th class="text-right">배출량 (kgCO2e)</th>
                        <th class="text-right">비율 (%)</th>
                    </template>
                    <template #body>
                        <tr v-for="(category, index) in detail.byCategory" :key="index">
                            <td>{{ category.categoryName || "-" }}</td>
                            <td>{{ category.categoryCode || "-" }}</td>
                            <td>
                                <span class="scope-badge" :class="`scope${getScopeNumber(category.scopeType)}`"
                                    >S{{ getScopeNumber(category.scopeType) }}</span
                                >
                            </td>
                            <td class="text-right">{{ category.calcLineCount || 0 }}건</td>
                            <td class="text-right item-value">
                                {{ formatEmission(category.emission, 1) }}
                            </td>
                            <td class="text-right item-percent">
                                {{ calculatePercentage(category.emission, detail.summary.totalEmission) }}%
                            </td>
                        </tr>
                    </template>
                </CommonTable>
            </b-modal>

            <!-- 시설별 모달 -->
            <b-modal
                v-model="showFacilitiesModalFlag"
                title="시설별 배출량"
                size="xl"
                centered
                hide-footer
                @hidden="closeFacilitiesModal"
            >
                <CommonTable width="100%" margin="0" maxHeight="60vh">
                    <template #header-cells>
                        <th>시설명</th>
                        <th class="text-right">포함비율 (%)</th>
                        <th class="text-right">Scope 1 (kgCO2e)</th>
                        <th class="text-right">Scope 2 (kgCO2e)</th>
                        <th class="text-right">Scope 3 (kgCO2e)</th>
                        <th class="text-right">합계 (kgCO2e)</th>
                    </template>
                    <template #body>
                        <tr v-for="(facility, index) in detail.byFacility" :key="index">
                            <td>{{ getFacilityName(facility) }}</td>
                            <td class="text-right item-percent">{{ facility.includePct }}%</td>
                            <td class="text-right item-value">
                                {{ formatEmission(facility.scope1Emission, 1) }}
                            </td>
                            <td class="text-right item-value">
                                {{ formatEmission(facility.scope2Emission, 1) }}
                            </td>
                            <td class="text-right item-value">
                                {{ formatEmission(facility.scope3Emission, 1) }}
                            </td>
                            <td class="text-right item-value emission-cell">
                                {{ formatEmission(facility.totalEmission, 1) }}
                            </td>
                        </tr>
                    </template>
                </CommonTable>
            </b-modal>

            <!-- 산정내역 전체 목록 (본문은 미리보기만, 스크롤은 모달 내부만) -->
            <b-modal
                v-model="showLinesListModalFlag"
                title="산정내역"
                size="xl"
                centered
                hide-footer
                @hidden="closeLinesListModal"
            >
                <CommonTable width="100%" margin="0" maxHeight="60vh">
                    <template #header-cells>
                        <th>Scope</th>
                        <th>시설</th>
                        <th>카테고리</th>
                        <th class="text-right">배출량 (kgCO2e)</th>
                        <th class="text-right">CO2</th>
                        <th class="text-right">CH4</th>
                        <th class="text-right">N2O</th>
                        <th class="text-right">HFCs</th>
                        <th class="text-right">PFCs</th>
                        <th class="text-right">SF6</th>
                        <th class="text-right">활동</th>
                        <th class="text-right">포함비율</th>
                        <th class="text-right">조정 전 (kg)</th>
                    </template>
                    <template #body>
                        <tr
                            v-for="(line, index) in sortedLinesByEmission"
                            :key="line.lineIdx || index"
                            class="lines-list-modal-row"
                            @click="openLineFromList(line)"
                        >
                            <td>
                                <span class="scope-pill">S{{ line.scope || 1 }}</span>
                            </td>
                            <td>{{ line.facilityName || getFacilityName(line) || "—" }}</td>
                            <td>{{ line.categoryName || line.categoryCode || "—" }}</td>
                            <td class="text-right item-value">{{ formatEmission(getLineEmission(line), 1) }}</td>
                            <td class="text-right">{{ formatEmission(getLineGasCo2eq(line, "CO2"), 4) }}</td>
                            <td class="text-right">{{ formatEmission(getLineGasCo2eq(line, "CH4"), 4) }}</td>
                            <td class="text-right">{{ formatEmission(getLineGasCo2eq(line, "N2O"), 4) }}</td>
                            <td class="text-right">{{ formatEmission(getLineGasCo2eq(line, "HFCs"), 4) }}</td>
                            <td class="text-right">{{ formatEmission(getLineGasCo2eq(line, "PFCs"), 4) }}</td>
                            <td class="text-right">{{ formatEmission(getLineGasCo2eq(line, "SF6"), 4) }}</td>
                            <td class="text-right">{{ line.calcLineCount }}건</td>
                            <td class="text-right item-percent">{{ line.includePct }}%</td>
                            <td class="text-right">{{ formatEmission(getLineRawEmission(line), 1) }}</td>
                        </tr>
                    </template>
                </CommonTable>
            </b-modal>

            <!-- 품질점검·불확도 가이드 모달 -->
            <b-modal
                v-model="showGuideModalFlag"
                title="품질점검·불확도 활용 가이드라인"
                size="lg"
                centered
                hide-footer
                body-class="guide-modal-body"
                @hidden="closeGuideModal"
            >
                <div class="guide-modal-content">
                    <markdown-it-vue :content="guideMarkdown"></markdown-it-vue>
                </div>
            </b-modal>
        </div>
    </div>
</template>

<script>
import CarbonCodes from "../../../codes";
import { fetchFacility } from "../../../services/utilityService.js";
import { fetchOrgBoundaryDetail, fetchOperBoundaryDetail } from "../../../services/boundaryService.js";
import InventoryDetailSkeleton from "./InventoryDetailSkeleton.vue";
import CommonTable from "../../common/CommonTable.vue";
import CustomDropdown from "../../common/CustomDropdown.vue";
import MarkdownItVue from "markdown-it-vue";
import "markdown-it-vue/dist/markdown-it-vue.css";
import { INVENTORY_QUALITY_UNCERTAINTY_GUIDE } from "../../../constants/inventoryGuideContent.js";
import carbonApi from "@/submodules/carbon-plugin/api";
import { unwrapResponse, extractErrorMessage } from "../../../utils/api.js";

export default {
    name: "InventoryDetailView",
    components: {
        InventoryDetailSkeleton,
        CommonTable,
        CustomDropdown,
        MarkdownItVue,
    },
    props: {
        detail: {
            type: Object,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        qualityLoading: {
            type: Boolean,
            default: false,
        },
        uncertaintyLoading: {
            type: Boolean,
            default: false,
        },
        qualityResult: {
            type: Object,
            default: null,
        },
        uncertaintyResult: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            facilityMap: {},
            orgBoundaryName: null,
            operBoundaryName: null,
            showLinesModalFlag: false,
            showLinesListModalFlag: false,
            showCategoriesModalFlag: false,
            showFacilitiesModalFlag: false,
            showGuideModalFlag: false,
            selectedLineDetail: null,
            showLineCalcDetailsModal: false,
            /** GET /inventories/lines/:lineIdx 응답 (GWP · 배출계수) */
            inventoryLineDetailPayload: null,
            lineDetailApiLoading: false,
            lineDetailApiError: null,
        };
    },
    computed: {
        headerActionItems() {
            return [
                {
                    icon: "fa-trash-alt",
                    label: "삭제",
                    variant: "danger",
                    onClick: () => this.$emit("delete"),
                },
            ];
        },
        isTotalEmissionZero() {
            if (!this.detail || !this.detail.summary) return false;
            const totalEmission = this.detail.summary.totalEmission;
            return totalEmission == null || totalEmission === 0 || Number(totalEmission) === 0;
        },
        qualityScore() {
            return this.qualityResult?.qualityScore ?? this.qualityResult?.overallScore ?? 0;
        },
        qualityStatusLabel() {
            const status = this.qualityResult?.overallQuality || this.qualityResult?.status || "unknown";
            switch (status) {
                case "excellent":
                    return "우수";
                case "good":
                    return "양호";
                case "fair":
                    return "보통";
                case "poor":
                    return "미흡";
                default:
                    return "N/A";
            }
        },
        qualityStatusClass() {
            const status = this.qualityResult?.overallQuality || this.qualityResult?.status;
            if (!status) return "status-unknown";
            return `status-${status}`;
        },
        qualitySummary() {
            return {
                totalChecks: this.qualityResult?.totalChecks || 0,
                passed: this.qualityResult?.passedChecks || 0,
                warnings: this.qualityResult?.warnings || 0,
                errors: this.qualityResult?.errors || 0,
                actionRequired: this.qualityResult?.summary?.actionRequired || false,
            };
        },
        uncertaintyLevelLabel() {
            const level = this.uncertaintyResult?.uncertainty?.level;
            switch (level) {
                case "low":
                    return "낮음";
                case "medium":
                    return "중간";
                case "high":
                    return "높음";
                default:
                    return level || "-";
            }
        },
        uncertaintyLevelKey() {
            return this.uncertaintyResult?.uncertainty?.level || "unknown";
        },
        /** 시설 합계 배출량 내림차순 (미리보기·모달 공통) */
        facilitiesSortedByEmission() {
            if (!this.detail?.byFacility) return [];
            return [...this.detail.byFacility].sort(
                (a, b) => (Number(b.totalEmission) || 0) - (Number(a.totalEmission) || 0),
            );
        },
        facilityPreview() {
            return this.facilitiesSortedByEmission;
        },
        /** 산정 라인 배출량 내림차순 */
        sortedLinesByEmission() {
            if (!this.detail?.lines?.length) return [];
            return [...this.detail.lines].sort((a, b) => {
                const ea = Number(this.getLineEmission(a)) || 0;
                const eb = Number(this.getLineEmission(b)) || 0;
                return eb - ea;
            });
        },
        /** 패널 내부 스크롤로 전체 행 표시 */
        linesPreview() {
            return this.sortedLinesByEmission;
        },
        categoryPreview() {
            return this.sortedCategories;
        },
        guideMarkdown() {
            return INVENTORY_QUALITY_UNCERTAINTY_GUIDE;
        },
        uncertaintyInterval() {
            return (
                this.uncertaintyResult?.uncertainty?.confidenceInterval || {
                    lower: 0,
                    upper: 0,
                    confidence: 95,
                }
            );
        },
        uncertaintyBarWidth() {
            if (!this.uncertaintyResult?.uncertainty?.percentage) return 0;
            const uncertainty = Number(this.uncertaintyResult.uncertainty.percentage);
            // 불확실성이 낮을수록 높은 바 (반전) - 최대 30% 기준
            if (uncertainty <= 5) return 100;
            if (uncertainty <= 10) return 80;
            if (uncertainty <= 20) return 60;
            if (uncertainty <= 30) return 40;
            return Math.max(20, 100 - uncertainty);
        },
        uncertaintyTopScopes() {
            if (!this.uncertaintyResult?.byScope) return [];
            return this.uncertaintyResult.byScope.slice(0, 3);
        },
        currentUncertaintyColor() {
            return "#52b788";
        },
        currentUncertaintyBgColor() {
            return "#f0fdf4";
        },
        currentUncertaintyTextColor() {
            return "#166534";
        },
        maxUncertaintyValue() {
            if (!this.uncertaintyResult?.byScope?.length) return "-";
            const maxItem = this.uncertaintyResult.byScope.reduce((max, item) => {
                return (item.uncertainty || 0) > (max.uncertainty || 0) ? item : max;
            }, this.uncertaintyResult.byScope[0]);
            return this.formatPercent(maxItem.uncertainty);
        },
        maxUncertaintyLabel() {
            if (!this.uncertaintyResult?.byScope?.length) return "-";
            const maxItem = this.uncertaintyResult.byScope.reduce((max, item) => {
                return (item.uncertainty || 0) > (max.uncertainty || 0) ? item : max;
            }, this.uncertaintyResult.byScope[0]);
            return `Scope ${maxItem.scope}`;
        },
        avgUncertaintyValue() {
            if (!this.uncertaintyResult?.byScope?.length) return "-";
            const avg =
                this.uncertaintyResult.byScope.reduce((sum, item) => sum + (item.uncertainty || 0), 0) /
                this.uncertaintyResult.byScope.length;
            return this.formatPercent(avg);
        },
        minUncertaintyValue() {
            if (!this.uncertaintyResult?.byScope?.length) return "-";
            const minItem = this.uncertaintyResult.byScope.reduce((min, item) => {
                return (item.uncertainty || 0) < (min.uncertainty || 0) ? item : min;
            }, this.uncertaintyResult.byScope[0]);
            return this.formatPercent(minItem.uncertainty);
        },
        minUncertaintyLabel() {
            if (!this.uncertaintyResult?.byScope?.length) return "-";
            const minItem = this.uncertaintyResult.byScope.reduce((min, item) => {
                return (item.uncertainty || 0) < (min.uncertainty || 0) ? item : min;
            }, this.uncertaintyResult.byScope[0]);
            return `Scope ${minItem.scope}`;
        },
        formattedCalculationDetails() {
            if (!this.selectedLineDetail?.calculationDetails) return "";
            try {
                // 이미 JSON 문자열인 경우 파싱 후 다시 포맷팅
                const parsed = JSON.parse(this.selectedLineDetail.calculationDetails);
                return JSON.stringify(parsed, null, 2);
            } catch (e) {
                // 파싱 실패 시 원본 문자열 반환
                return this.selectedLineDetail.calculationDetails;
            }
        },
        lineDetailModalTitle() {
            const line = this.selectedLineDetail;
            if (!line) return "산정내역 상세";
            const name = line.facilityName || this.getFacilityName(line) || line.facilityCode || "시설";
            const cat = line.categoryName || line.categoryCode || "";
            return cat ? `산정내역 상세 · ${name} · ${cat}` : `산정내역 상세 · ${name}`;
        },
        /** 가스별 질량(kg) + CO2eq(kgCO2e) — 6가스 전 행 */
        lineGasDetailRows() {
            const line = this.selectedLineDetail;
            if (!line) return [];
            const defs = [
                { gasKey: "CO2", co2eqKey: "CO2eqCO2" },
                { gasKey: "CH4", co2eqKey: "CO2eqCH4" },
                { gasKey: "N2O", co2eqKey: "CO2eqN2O" },
                { gasKey: "HFCs", co2eqKey: "CO2eqHFCs" },
                { gasKey: "PFCs", co2eqKey: "CO2eqPFCs" },
                { gasKey: "SF6", co2eqKey: "CO2eqSF6" },
            ];
            return defs.map(({ gasKey, co2eqKey }) => {
                const fromBreakdown = line.gasBreakdown?.[gasKey];
                const top = line[gasKey];
                const rawMass = fromBreakdown != null && fromBreakdown !== "" ? fromBreakdown : top;
                let massKg = null;
                if (rawMass != null && rawMass !== "") {
                    const n = Number(rawMass);
                    massKg = Number.isFinite(n) ? n : null;
                }
                const co2eqRaw = line[co2eqKey];
                const co2eqKg = co2eqRaw != null && co2eqRaw !== "" ? Number(co2eqRaw) : null;
                return {
                    gasKey,
                    gasLabel: gasKey,
                    massKg,
                    co2eqKg: co2eqKg != null && Number.isFinite(co2eqKg) ? co2eqKg : null,
                };
            });
        },
        /** Σ 가스 질량(kg) — API 제공값만 표시 */
        lineRawMassSumKg() {
            const line = this.selectedLineDetail;
            if (!line) return null;
            const direct = line.massKg ?? line.rawEmission;
            if (direct != null && direct !== "") return Number(direct);
            return null;
        },
        /** CO2eq 합계 표시 — API 제공값만 표시 */
        lineCo2eqTotalDisplay() {
            const line = this.selectedLineDetail;
            if (!line) return null;
            const t = line.CO2eqTotal ?? line.co2eqTotal;
            if (t != null && t !== "") {
                const n = Number(t);
                return Number.isFinite(n) ? n : null;
            }
            return null;
        },
        /** 식별 정보: 시설이름(시설코드) */
        lineDetailFacilityDisplay() {
            const line = this.selectedLineDetail;
            if (!line) return "";
            const name = line.facilityName || this.getFacilityName(line) || "";
            const code = line.facilityCode;
            if (name && code) return `${name}(${code})`;
            return name || code || "";
        },
        /** 식별 정보: #카테고리id 카테고리이름(카테고리코드) */
        lineDetailCategoryDisplay() {
            const line = this.selectedLineDetail;
            if (!line) return "";
            const id = line.categoryIdx;
            const name = line.categoryName || "";
            const code = line.categoryCode || "";
            const nameWithCode = code ? `${name}(${code})` : name;
            if (id != null && id !== "") return `#${id} ${nameWithCode}`;
            return nameWithCode || "";
        },
        lineDetailTraceUnavailableMessage() {
            const r = this.inventoryLineDetailPayload?.traceUnavailableReason;
            if (r === "RUN_LINK_MISSING") {
                return "이 인벤토리는 계산 실행(run)과 연결되어 있지 않아 배출계수 추적을 완료할 수 없습니다. 아래는 인벤토리에 기록된 GWP 프로필 정보입니다.";
            }
            return r || "";
        },
        /** 서버가 co2eq 기여가 가장 큰 1건을 내려줌 */
        lineDetailPrimaryFactorSummary() {
            const list = this.inventoryLineDetailPayload?.factorSummary;
            if (!Array.isArray(list) || !list.length) return null;
            return list[0];
        },
        scopeData() {
            if (!this.detail?.summary) return [];
            const scope1 = Number(this.detail.summary.scope1Emission) || 0;
            const scope2 = Number(this.detail.summary.scope2Emission) || 0;
            const scope3 = Number(this.detail.summary.scope3Emission) || 0;
            const total = scope1 + scope2 + scope3;

            return [
                {
                    name: "Scope 1",
                    value: scope1,
                    percentage: total > 0 ? ((scope1 / total) * 100).toFixed(1) : "0.0",
                    color: "#081c15",
                },
                {
                    name: "Scope 2",
                    value: scope2,
                    percentage: total > 0 ? ((scope2 / total) * 100).toFixed(1) : "0.0",
                    color: "#2d6a4f",
                },
                {
                    name: "Scope 3",
                    value: scope3,
                    percentage: total > 0 ? ((scope3 / total) * 100).toFixed(1) : "0.0",
                    color: "#52b788",
                },
            ];
        },
        uncertaintyRangeMin() {
            if (!this.uncertaintyResult || !this.detail?.summary?.totalEmission) return 0;
            const lower = Number(this.uncertaintyInterval.lower) || 0;
            const upper = Number(this.uncertaintyInterval.upper) || 0;
            const current = Number(this.detail.summary.totalEmission) || 0;
            return Math.min(lower, current, upper);
        },
        uncertaintyRangeMax() {
            if (!this.uncertaintyResult || !this.detail?.summary?.totalEmission) return 0;
            const lower = Number(this.uncertaintyInterval.lower) || 0;
            const upper = Number(this.uncertaintyInterval.upper) || 0;
            const current = Number(this.detail.summary.totalEmission) || 0;
            return Math.max(lower, current, upper);
        },
        uncertaintyLowerPercent() {
            const range = this.uncertaintyRangeMax - this.uncertaintyRangeMin;
            if (range === 0) return 0;
            const lower = Number(this.uncertaintyInterval.lower) || 0;
            return ((lower - this.uncertaintyRangeMin) / range) * 100;
        },
        uncertaintyMiddlePercent() {
            const range = this.uncertaintyRangeMax - this.uncertaintyRangeMin;
            if (range === 0) return 0;
            const lower = Number(this.uncertaintyInterval.lower) || 0;
            const upper = Number(this.uncertaintyInterval.upper) || 0;
            return ((upper - lower) / range) * 100;
        },
        uncertaintyUpperPercent() {
            const range = this.uncertaintyRangeMax - this.uncertaintyRangeMin;
            if (range === 0) return 0;
            const upper = Number(this.uncertaintyInterval.upper) || 0;
            return ((this.uncertaintyRangeMax - upper) / range) * 100;
        },
        currentEmissionPercent() {
            const range = this.uncertaintyRangeMax - this.uncertaintyRangeMin;
            if (range === 0) return 50;
            const current = Number(this.detail.summary.totalEmission) || 0;
            const position = ((current - this.uncertaintyRangeMin) / range) * 100;
            return Math.max(0, Math.min(100, position));
        },
        sortedCategories() {
            if (!this.detail?.byCategory) return [];
            return [...this.detail.byCategory]
                .filter((cat) => {
                    const emission = Number(cat.emission) || 0;
                    return emission > 0;
                })
                .sort((a, b) => {
                    const emissionA = Number(a.emission) || 0;
                    const emissionB = Number(b.emission) || 0;
                    return emissionB - emissionA;
                });
        },
        totalActivityCount() {
            if (!this.detail?.lines?.length) return 0;
            return this.detail.lines.reduce((sum, line) => sum + (line.activityCount || 0), 0);
        },
        categoryActivityStats() {
            if (!this.detail?.byCategory) return [];
            return this.detail.byCategory
                .filter((cat) => (cat.activityCount || 0) > 0)
                .sort((a, b) => (b.activityCount || 0) - (a.activityCount || 0));
        },
        execStatLabel() {
            return CarbonCodes.execStat.getLabel(this.detail?.execStat) || this.detail?.execStat || "-";
        },
    },
    watch: {
        detail: {
            handler(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    this.loadFacilities();
                    this.loadBoundaries();
                }
            },
        },
    },
    mounted() {
        window.addEventListener("resize", this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
    },
    methods: {
        formatPeriod(period) {
            if (!period || String(period).length !== 6) return period;
            return `${String(period).slice(0, 4)}.${String(period).slice(4, 6)}`;
        },
        formatEmission(value, decimals = 2) {
            if (value == null) return "-";
            return Number(value).toLocaleString("ko-KR", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            });
        },
        formatLineModalEmission(value, decimals = 2) {
            if (value == null || value === "") return "";
            return this.formatEmission(value, decimals);
        },
        /** 260311: gasCode 라벨 (CarbonCodes 또는 코드값 그대로) */
        getGasLabel(gasCode) {
            return CarbonCodes.gasCode?.getLabel?.(gasCode) ?? gasCode ?? "-";
        },
        /** 산정내역 목록용: 가스별 CO2eq(kgCO2e) 값 추출 */
        getLineGasCo2eq(line, gasKey) {
            if (!line || !gasKey) return null;
            const keyMap = {
                CO2: "CO2eqCO2",
                CH4: "CO2eqCH4",
                N2O: "CO2eqN2O",
                HFCs: "CO2eqHFCs",
                PFCs: "CO2eqPFCs",
                SF6: "CO2eqSF6",
            };
            const fromBreakdown = line.gasBreakdown?.[gasKey]?.co2eqKg;
            if (fromBreakdown != null && fromBreakdown !== "") return Number(fromBreakdown);
            const co2eqKey = keyMap[gasKey];
            const value = co2eqKey ? line[co2eqKey] : null;
            return value != null && value !== "" ? Number(value) : null;
        },
        /** 가스 코드 → 화학식 표기(괄호용, 유니코드 아래첨자) */
        getGasFormulaDisplay(gasKey) {
            if (!gasKey) return "";
            const k = String(gasKey).toUpperCase();
            const map = {
                CO2: "CO₂",
                CH4: "CH₄",
                N2O: "N₂O",
                HFCs: "HFCs",
                PFCs: "PFCs",
                SF6: "SF₆",
            };
            return map[k] ?? String(gasKey);
        },
        /** 260311: co2eqAmount(신규) → resultEmission → adjustedEmission fallback */
        getLineEmission(line) {
            return line?.CO2eqTotal ?? line?.adjustedEmission;
        },
        /** 260311: massKg(신규) → rawEmission → resultEmission → adjustedEmission fallback */
        getLineRawEmission(line) {
            return line?.massKg ?? line?.rawEmission ?? line?.resultEmission ?? line?.adjustedEmission;
        },
        calculatePercentage(value, total) {
            if (!total || total === 0) return "0.0";
            return ((value / total) * 100).toFixed(1);
        },
        formatDateTime(dateTimeStr) {
            if (!dateTimeStr) return "";
            try {
                const date = new Date(dateTimeStr);
                if (Number.isNaN(date.getTime())) return dateTimeStr;
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                return `${year}.${month}.${day} ${hours}:${minutes}`;
            } catch (e) {
                return dateTimeStr;
            }
        },
        formatPercent(value, decimals = 2) {
            if (value == null || value === "") return "-";
            const num = Number(value);
            if (Number.isNaN(num)) return "-";
            return `${num.toFixed(decimals)}%`;
        },
        requestQualityCheck() {
            if (!this.detail || this.qualityLoading) return;
            this.$emit("quality-check", this.detail.inventoryIdx);
        },
        requestUncertaintyCheck() {
            if (!this.detail || this.uncertaintyLoading) return;
            this.$emit("uncertainty-check", this.detail.inventoryIdx);
        },

        async openLineDetailModal(line) {
            this.selectedLineDetail = line;
            this.showLineCalcDetailsModal = false;
            this.showLinesModalFlag = true;
            this.inventoryLineDetailPayload = null;
            this.lineDetailApiError = null;
            if (line?.lineIdx == null) {
                this.lineDetailApiError = "라인 식별자가 없어 GWP·배출계수 데이터를 불러올 수 없습니다.";
                return;
            }
            await this.fetchInventoryLineDetail(line.lineIdx);
        },
        async fetchInventoryLineDetail(lineIdx) {
            this.lineDetailApiLoading = true;
            this.lineDetailApiError = null;
            try {
                const res = await carbonApi.inventory.getInventoryLine(lineIdx);
                this.inventoryLineDetailPayload = unwrapResponse(res, {
                    defaultMessage: "산정 라인 상세를 불러오지 못했습니다.",
                });
            } catch (e) {
                this.inventoryLineDetailPayload = null;
                this.lineDetailApiError = extractErrorMessage(e, "산정 라인 상세를 불러오지 못했습니다.");
            } finally {
                this.lineDetailApiLoading = false;
            }
        },
        showCategoriesModal() {
            this.showCategoriesModalFlag = true;
        },
        showFacilitiesModal() {
            this.showFacilitiesModalFlag = true;
        },
        closeLinesListModal() {
            this.showLinesListModalFlag = false;
        },
        openLineFromList(line) {
            this.showLinesListModalFlag = false;
            this.$nextTick(() => this.openLineDetailModal(line));
        },
        showQualityDetail() {
            // 품질 점검 상세 모달 열기
            this.$emit("quality-check", this.detail.inventoryIdx);
        },
        closeLinesModal() {
            this.showLineCalcDetailsModal = false;
            this.showLinesModalFlag = false;
            this.selectedLineDetail = null;
            this.inventoryLineDetailPayload = null;
            this.lineDetailApiLoading = false;
            this.lineDetailApiError = null;
        },
        closeCategoriesModal() {
            this.showCategoriesModalFlag = false;
        },
        closeFacilitiesModal() {
            this.showFacilitiesModalFlag = false;
        },
        showGuideModal() {
            this.showGuideModalFlag = true;
        },
        closeGuideModal() {
            this.showGuideModalFlag = false;
        },
        getScopeNumber(scopeType) {
            if (!scopeType) return 1;
            if (typeof scopeType === "number") return scopeType;
            if (typeof scopeType === "string") {
                const match = scopeType.match(/\d+/);
                return match ? parseInt(match[0]) : 1;
            }
            return 1;
        },
        async loadFacilities() {
            if (!this.detail?.byFacility?.length) return;

            const facilityCodes = this.detail.byFacility
                .map((facility) => facility.facilityCode || facility.facilityIdx)
                .filter((code) => code != null);

            const uniqueCodes = [...new Set(facilityCodes)];

            const facilityPromises = uniqueCodes.map(async (code) => {
                try {
                    const facilityInfo = await fetchFacility(code);
                    return { code, info: facilityInfo };
                } catch (error) {
                    console.warn(`시설 조회 실패 (${code}):`, error);
                    return { code, info: null };
                }
            });

            const results = await Promise.all(facilityPromises);

            const newMap = {};
            results.forEach(({ code, info }) => {
                if (info) {
                    newMap[code] = info;
                }
            });

            this.facilityMap = newMap;
        },
        getFacilityName(facility) {
            const code = facility.facilityCode || facility.facilityIdx;
            if (this.facilityMap[code]) {
                return this.facilityMap[code].facilityName || code;
            }
            return code || "-";
        },
        async loadBoundaries() {
            if (!this.detail) return;

            if (this.detail.orgBoundaryIdx) {
                try {
                    const orgBoundary = await fetchOrgBoundaryDetail(this.detail.orgBoundaryIdx);
                    this.orgBoundaryName = orgBoundary?.orgBoundaryName || null;
                } catch (error) {
                    console.warn("조직 경계 조회 실패:", error);
                    this.orgBoundaryName = null;
                }
            }

            if (this.detail.operBoundaryIdx) {
                try {
                    const operBoundary = await fetchOperBoundaryDetail(this.detail.operBoundaryIdx);
                    this.operBoundaryName = operBoundary?.operBoundaryName || null;
                } catch (error) {
                    console.warn("운영 경계 조회 실패:", error);
                    this.operBoundaryName = null;
                }
            }
        },
        handleResize() {
            // 차트 리사이즈는 필요시 추가
        },
    },
};
</script>

<style scoped>
.inventory-detail {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    font-size: 12px;
    background: #fafafa;
}

/* Empty 상태 */
.empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #999999;
    text-align: center;
    font-size: 12px;
}

.empty-state i {
    font-size: 48px;
    color: #cccccc;
    margin-bottom: 12px;
}

.empty-state p {
    margin: 0 0 4px 0;
    font-weight: 600;
}

.empty-state small {
    font-size: 11px;
    color: #cccccc;
}

/* 상세 컨테이너 — 부모(우측 패널) 높이에 맞춤, 넘치지 않게 */
.detail-container {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-height: 0;
    overflow: hidden;
}

/* 헤더 */
.detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    background: #ffffff;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;
}

.header-info {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

/* 조직경계 = 핵심 제목 */
.header-primary {
    flex-shrink: 0;
}

.header-title {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #1a1a1a;
}

.header-title--muted {
    font-weight: 600;
    color: #999999;
}

/* 기간 · 운영경계 · ID = 뱃지 */
.header-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.header-meta .header-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 500;
    color: #555555;
    background: #f0f0f0;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
}

.header-period {
    color: #555555;
}

.header-meta .header-oper {
    color: #666666;
}

.header-id {
    font-size: 11px;
    color: #888888;
    margin-left: auto;
}

.header-actions {
    display: flex;
}

.action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    font-size: 15px;
    color: #999999;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition:
        color 0.15s ease,
        background 0.15s ease;
}

.action-btn:hover:not(:disabled) {
    color: #555555;
    background: #f0f0f0;
}

.action-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.action-btn.delete-btn {
    color: #999999;
}

.action-btn.delete-btn:hover:not(:disabled) {
    color: #c62828;
    background: #ffebee;
}

/* Body — 우측 패널 안에서만 스크롤 없이 배치 (남은 높이 flex 분배) */
.detail-body {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 6px 8px;
    min-height: 0;
    overflow: hidden;
}

/* 상단 메트릭 카드들 — 작은 뷰포트용 고밀도 */
.top-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    flex: 0 0 auto;
    min-height: 0;
}

/* 메인 메트릭 카드 (통일된 디자인) */
.main-metric-card {
    position: relative;
    background: linear-gradient(135deg, #52b788 0%, #40916c 100%);
    border-radius: 6px;
    padding: 8px 10px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(82, 183, 136, 0.18);
    transition: box-shadow 0.15s ease;
}

.main-metric-card:hover {
    box-shadow: 0 2px 8px rgba(82, 183, 136, 0.25);
}

.main-metric-card.quality-card {
    background: linear-gradient(135deg, #52b788 0%, #40916c 100%);
    box-shadow: 0 2px 8px rgba(82, 183, 136, 0.2);
}

.main-metric-card.quality-card:hover {
    box-shadow: 0 2px 8px rgba(82, 183, 136, 0.25);
}

.main-metric-card.uncertainty-card {
    background: linear-gradient(135deg, #52b788 0%, #40916c 100%);
    box-shadow: 0 2px 8px rgba(82, 183, 136, 0.2);
}

.main-metric-card.uncertainty-card:hover {
    box-shadow: 0 2px 8px rgba(82, 183, 136, 0.25);
}

.metric-bg-icon {
    position: absolute;
    right: -20px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 110px;
    color: rgba(255, 255, 255, 0.1);
    pointer-events: none;
}

.main-metric-content {
    position: relative;
    z-index: 1;
    color: #ffffff;
}

.main-metric-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.main-metric-label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.95);
}

.main-metric-timestamp {
    font-size: 10px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.75);
}

.main-metric-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    background: rgba(255, 255, 255, 0.15);
    border: none;
    border-radius: 4px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.9);
    transition: all 0.15s ease;
}

.main-metric-btn:hover {
    background: rgba(255, 255, 255, 0.25);
    color: #ffffff;
}

.main-metric-btn .material-icons {
    font-size: 17px;
}

.main-metric-value-section {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 0;
}

.main-metric-value {
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.main-metric-number {
    font-size: 26px;
    font-weight: 800;
    line-height: 1.05;
}

.main-metric-unit {
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
}

.main-metric-score {
    font-size: 26px;
    font-weight: 800;
    line-height: 1.05;
    color: #ffffff;
}

.main-metric-status {
    font-size: 9px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 12px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    display: inline-block;
    margin-left: 12px;
}

.main-metric-loading,
.main-metric-empty {
    text-align: center;
    padding: 8px 0;
    font-size: 11px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
}

.main-metric-action-btn {
    padding: 8px 16px;
    font-size: 11px;
    font-weight: 600;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.main-metric-action-btn:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
}

.main-metric-result {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.main-metric-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
    width: 100%;
    margin-top: 3px;
    padding-top: 4px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.main-stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.main-stat-label {
    font-size: 10px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.75);
}

.main-stat-value {
    font-size: 11px;
    font-weight: 800;
    color: #ffffff;
}

.main-stat-value.stat-success {
    color: #dcfce7;
}

.main-stat-value.stat-warning {
    color: #fef3c7;
}

.main-stat-value.stat-error {
    color: #fecaca;
}

/* 하단: 남은 높이만 사용, 행 수 제한으로 스크롤 없이 한 화면에 맞춤 */
.lower-data {
    flex: 1 1 0;
    min-height: 0;
    display: grid;
    grid-template-rows: minmax(0, 1.05fr) minmax(0, 0.95fr);
    gap: 6px;
    overflow: hidden;
}

.inv-panel {
    background: #ffffff;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.inv-panel--primary {
    min-height: 0;
    overflow: hidden;
    border-color: #cbd5e1;
    box-shadow:
        0 0 0 1px rgba(100, 116, 139, 0.15),
        0 1px 4px rgba(15, 23, 42, 0.06);
}

.inv-panel--primary .inv-panel-head {
    background: #f8fafc;
    border-bottom-color: #dbe3ee;
}

.inv-panel--primary .inv-panel-title {
    color: #0f172a;
    font-weight: 800;
}

.inv-panel-row {
    min-height: 0;
    overflow: hidden;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 6px;
}

.inv-panel--secondary {
    min-height: 0;
}

.inv-panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 10px;
    min-height: 38px;
    height: 38px;
    box-sizing: border-box;
    border-bottom: 1px solid #eeeeee;
    flex-shrink: 0;
}

.inv-panel-head-text {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
}

.inv-panel-title {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.01em;
}

.inv-panel-meta {
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
}

.inv-panel-link {
    flex-shrink: 0;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    cursor: pointer;
    transition:
        background 0.12s ease,
        border-color 0.12s ease;
}

.inv-panel-link:hover {
    background: #e5e7eb;
    border-color: #d1d5db;
}

.inv-panel-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    color: #6b7280;
    transition:
        background 0.12s ease,
        color 0.12s ease;
}

.inv-panel-icon-btn:hover {
    background: #f3f4f6;
    color: #111827;
}

.inv-panel-icon-btn .material-icons {
    font-size: 18px;
}

.inv-panel-body {
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
}

.scope-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.75rem;
    padding: 1px 6px;
    font-size: 10px;
    font-weight: 600;
    color: #374151;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
}

.lines-preview-wrap {
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow-y: auto;
    overflow-x: hidden;
}

.lines-preview-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    table-layout: fixed;
}

.lines-preview-th {
    padding: 6px 0px;
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-align: center;
    border-bottom: 1px solid #e5e7eb;
    position: sticky;
    top: 0;
    z-index: 2;
    background: #ffffff;
}

.lines-preview-th--scope {
    width: 2.2rem;
}

.lines-preview-th--num {
    text-align: center;
    width: 3.6rem;
}

.lines-preview-th--main {
    width: 22%;
    min-width: 0;
}

.lines-preview-th--gas {
    width: 3.3rem;
}

.lines-preview-th--activity {
    width: 2.5rem;
}

.lines-preview-row {
    cursor: pointer;
    transition: background 0.1s ease;
}

.lines-preview-row:hover {
    background: #f9fafb;
}

.lines-preview-row:focus-visible {
    outline: 2px solid #94a3b8;
    outline-offset: -2px;
}

.lines-preview-td {
    padding: 6px 0px;
    vertical-align: middle;
    border-bottom: 1px solid #f3f4f6;
    color: #1f2937;
    text-align: center;
}

.lines-preview-td--main {
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.lines-preview-facility {
    font-weight: 600;
}

.lines-preview-sep {
    margin: 0 2px;
    color: #9ca3af;
}

.lines-preview-cat {
    font-weight: 500;
    color: #4b5563;
}

.lines-preview-td--num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
}

.lines-preview-td--activity {
    white-space: nowrap;
    text-align: center;
}

.lines-preview-th--emission {
    color: #1f2937;
    background: #eef6f1;
}

.lines-preview-td--emission {
    color: #166534;
    font-weight: 800;
    background: #f4fbf7;
}

.lines-preview-footnote {
    margin: 0;
    font-size: 9px;
    color: #6b7280;
    line-height: 1.3;
}

.lines-list-modal-row {
    cursor: pointer;
    transition: background 0.1s ease;
}

.lines-list-modal-row:hover {
    background: #f9fafb;
}

.inv-mini-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-height: 0;
}

.inv-mini-row {
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    grid-template-rows: auto auto;
    align-items: center;
    column-gap: 6px;
    row-gap: 1px;
    padding: 6px 9px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 11px;
}

.inv-mini-row:last-child {
    border-bottom: none;
}

.inv-mini-name {
    min-width: 0;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.inv-mini-val {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #111827;
    text-align: right;
    white-space: nowrap;
}

.inv-mini-unit {
    margin-left: 2px;
    font-size: 9px;
    font-weight: 600;
    color: #6b7280;
}

.inv-mini-pct {
    width: 2.5rem;
    text-align: right;
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
}

.inv-mini-bar {
    grid-column: 1 / -1;
    height: 3px;
    background: #f3f4f6;
    border-radius: 2px;
}

.inv-mini-bar-fill {
    height: 100%;
    background: #64748b;
    border-radius: 2px;
    max-width: 100%;
    transition: width 0.2s ease;
}

.inv-mini-row--facility {
    grid-template-columns: 1.5rem 1fr auto auto;
}

.inv-mini-rank {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    font-size: 10px;
    font-weight: 700;
    color: #4b5563;
    background: #f3f4f6;
    border-radius: 4px;
}

.inv-mini-scopes {
    grid-column: 2 / -1;
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
}

.scope-dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1rem;
    height: 1rem;
    padding: 0 3px;
    font-size: 9px;
    font-weight: 600;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    border-radius: 3px;
    background: #fafafa;
}

/* 활동 데이터 리스트 */
.activity-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.activity-item {
    padding: 8px 10px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    transition: all 0.15s ease;
}

.activity-item-main {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
}

.activity-item-name {
    font-size: 11px;
    font-weight: 700;
    color: #1a1a1a;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.activity-item-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
}

.activity-item-count {
    font-size: 11px;
    font-weight: 700;
    color: #52b788;
}

/* 품질 점검 요약 */
.quality-summary {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 8px 0;
}

.quality-summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 10px;
    background: #fafafa;
    border-radius: 6px;
}

.quality-summary-label {
    font-size: 11px;
    font-weight: 600;
    color: #666666;
}

.quality-summary-value {
    font-size: 16px;
    font-weight: 800;
    color: #1a1a1a;
}

.quality-summary-value.status-excellent {
    color: #52b788;
}

.quality-summary-value.status-good {
    color: #40916c;
}

.quality-summary-value.status-fair {
    color: #f59e0b;
}

.quality-summary-value.status-poor {
    color: #ef4444;
}

.quality-summary-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.quality-stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 8px;
    background: #fafafa;
    border-radius: 6px;
}

.quality-stat-value {
    font-size: 14px;
    font-weight: 800;
}

.quality-stat-value.stat-success {
    color: #52b788;
}

.quality-stat-value.stat-warning {
    color: #f59e0b;
}

.quality-stat-value.stat-error {
    color: #ef4444;
}

.quality-stat-label {
    font-size: 10px;
    font-weight: 600;
    color: #666666;
}

/* 불확도 요약 */
.uncertainty-summary {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 8px 0;
}

.uncertainty-summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 10px;
    background: #fafafa;
    border-radius: 6px;
}

.uncertainty-summary-label {
    font-size: 11px;
    font-weight: 600;
    color: #666666;
}

.uncertainty-summary-value {
    font-size: 14px;
    font-weight: 800;
    color: #52b788;
}

.uncertainty-scope-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.uncertainty-scope-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
}

.uncertainty-scope-label {
    font-size: 11px;
    font-weight: 600;
    color: #666666;
}

.uncertainty-scope-value {
    font-size: 11px;
    font-weight: 700;
    color: #52b788;
}

/* 계산 정보 */
.info-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 10px;
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
}

.info-label {
    font-size: 11px;
    font-weight: 600;
    color: #666666;
}

.info-value {
    font-size: 11px;
    font-weight: 700;
    color: #1a1a1a;
}

.status-badge {
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
}

.status-badge.status-completed {
    background: #dcfce7;
    color: #166534;
}

.status-badge.status-processing {
    background: #dbeafe;
    color: #1e40af;
}

.status-badge.status-failed {
    background: #fee2e2;
    color: #991b1b;
}

.status-badge.status-pending {
    background: #fef3c7;
    color: #92400e;
}

.status-badge.status-unknown {
    background: #f3f4f6;
    color: #6b7280;
}

/* Empty 상태 */
.content-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    font-size: 11px;
    font-weight: 600;
    color: #999999;
    text-align: center;
}

/* 산정내역 상세 모달 — 간결한 위계 */
:deep(.line-detail-modal-dialog) {
    max-width: min(1280px, 96vw);
}

.line-detail-modal-shell {
    margin: -16px;
    font-size: 12px;
    line-height: 1.5;
    color: #111827;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
}

.line-detail-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 16px;
    border-bottom: 1px solid #e5e7eb;
    background: #ffffff;
}

.line-detail-header-main {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.line-detail-header-title {
    font-size: 16px;
    font-weight: 800;
    color: #111827;
}

.line-detail-header-sep {
    color: #d1d5db;
}

.line-detail-header-sub {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
}

.line-detail-header-cat {
    font-size: 11px;
    font-weight: 700;
    color: #166534;
}

.line-detail-header-close {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: #6b7280;
}

.line-detail-header-close:hover {
    background: #f3f4f6;
    color: #374151;
}

.line-detail-modal-scroll {
    max-height: min(74vh, 900px);
    overflow-y: auto;
    padding: 14px 12px 12px 16px;
}

.line-detail-modal-scroll--dense {
    padding-right: 12px;
}

.line-detail-grid {
    display: grid;
    gap: 12px;
}

.line-detail-grid--summary {
    grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
    margin-bottom: 12px;
}

.line-detail-grid--factor {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.35fr);
    gap: 10px;
}

.line-detail-grid--bottom {
    grid-template-columns: minmax(0, 1.9fr) minmax(0, 1fr);
}

.line-detail-factor-col {
    min-width: 0;
    padding: 10px 12px;
    background: #ffffff;
}

.line-detail-section {
    margin-bottom: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #ffffff;
}

.line-detail-section:last-child {
    margin-bottom: 0;
}

.line-detail-section--summary {
    padding: 0;
    border: none;
    background: transparent;
}

.line-detail-section--factor {
    padding: 0;
    border: none;
    background: transparent;
}

.line-detail-summary-card {
    display: flex;
    justify-content: space-between;
    gap: 10px 14px;
    flex-wrap: wrap;
    align-items: center;
}

.line-detail-summary-card--hero {
    padding: 14px 16px;

    border-radius: 8px;
    background: linear-gradient(140deg, #f0fdf4 0%, #e6f6ed 100%);
    box-shadow: 0 2px 8px rgba(21, 128, 61, 0.08);
}

.line-detail-summary-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.line-detail-summary-label {
    font-size: 11px;
    font-weight: 700;
    color: #285d46;
}

.line-detail-summary-value-wrap {
    display: flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
}

.line-detail-summary-value {
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
}

.line-detail-summary-unit {
    font-size: 12px;
    font-weight: 700;
    color: #4b5563;
}

.line-detail-summary-stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(120px, 1fr));
    gap: 8px;
    min-width: 280px;
    padding-left: 12px;
    margin-left: 4px;
    border-left: 1px solid rgba(22, 101, 52, 0.12);
}

.line-detail-summary-stat {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 8px 10px 7px;
    border-radius: 4px;
}

.line-detail-meta-grid-card {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    background: #f8fafc;
}

.line-detail-meta-grid-cell {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px 10px;
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
}

.line-detail-meta-grid-cell .line-detail-meta-l {
    display: block;
    padding-bottom: 0;
    font-size: 10px;
    color: #6b7280;
}

.line-detail-meta-grid-cell .line-detail-meta-v {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #111827;
}

.line-detail-meta-grid-cell:nth-child(2n) {
    border-right: none;
}

.line-detail-meta-grid-cell:nth-last-child(-n + 2) {
    border-bottom: none;
}

.line-detail-summary-stat-k {
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
}

.line-detail-summary-stat-v {
    font-size: 13px;
    font-weight: 700;
    color: #111827;
    font-variant-numeric: tabular-nums;
}

/* 가스 본문 래퍼 — 바깥 테두리 없음(히어로·표에만 테두리) */
.line-detail-gas-panel {
    padding: 0;
}

.line-detail-section--gas .line-detail-section-title--secondary {
    margin-bottom: 8px;
}

.line-detail-api-status {
    padding: 9px 11px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #f9fafb;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
}

.line-detail-api-status--error {
    background: #fef2f2;
    border-color: #fecaca;
    color: #991b1b;
}

.line-detail-api-status--muted {
    color: #9ca3af;
    font-weight: 500;
}

.line-detail-trace-note {
    margin: 0 0 8px;
    padding: 6px 8px;
    border: 1px solid #fde68a;
    border-radius: 4px;
    background: #fffbeb;
    font-size: 11px;
    font-weight: 600;
    color: #92400e;
    line-height: 1.4;
}

.line-detail-trace-meta {
    margin-bottom: 8px;
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

.line-detail-trace-meta-sep {
    margin: 0 4px;
    color: #cbd5e1;
}

.line-detail-meta-v--muted {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
}

.line-detail-meta-v--emph {
    color: #14532d;
    font-weight: 800;
}

.line-factor-table-wrap {
    margin-top: 8px;
}

.line-factor-table-wrap--components {
    margin-top: 10px;
}

.line-factor-primary {
    margin-top: 8px;
}

.line-factor-meta-table-wrap {
    margin-top: 6px;
    overflow-x: auto;
}

.line-factor-meta-table {
    width: 100%;
    min-width: 420px;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 12px;
    border-radius: 8px;
    background: #f3f5f8;
}

.line-factor-meta-table--factor {
}

.line-factor-meta-table thead th {
    padding: 6px 8px 7px;
    border-bottom: none;
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
    text-align: center;
    white-space: nowrap;
    background: transparent;
}

.line-factor-meta-table tbody td {
    padding: 8px 8px 4px;
    font-weight: 700;
    color: #111827;
    border-bottom: none;
    vertical-align: top;
    word-break: break-word;
    text-align: center;
    background: transparent;
}

.line-factor-meta-table--horizontal tbody td {
    white-space: nowrap;
}

.line-factor-meta-table--factor tbody td:first-child {
    white-space: normal;
}

.line-factor-meta-value--emph {
    color: #166534;
    font-weight: 800;
    text-align: left;
}

.line-factor-meta-value--num {
    text-align: left;
}

.line-factor-table {
    width: 100%;
    min-width: 420px;
    border-collapse: collapse;
    font-size: 12px;
}

.line-factor-table th,
.line-factor-table td {
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    vertical-align: top;
}

.line-factor-table thead th {
    background: #f8fafc;
    font-weight: 700;
    color: #374151;
    text-align: center;
}

.line-factor-table tbody tr:nth-child(even) td {
    background: #fcfcfd;
}

.line-factor-table tbody td {
    text-align: center;
}

.line-factor-table tbody .text-right {
    text-align: left;
}

.line-factor-num {
    font-variant-numeric: tabular-nums;
    color: #374151;
    text-align: left;
}

.line-factor-remarks {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
    word-break: break-word;
}

.line-factor-table--components {
    min-width: 640px;
}

.line-detail-section-title {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 700;
    color: #374151;
    letter-spacing: 0;
}

.line-detail-section-title--secondary {
    font-size: 12px;
    font-weight: 700;
    color: #4b5563;
    text-transform: none;
    letter-spacing: 0;
    margin-bottom: 8px;
}

.line-detail-section-title--compact {
    margin: 0 0 6px;
    font-size: 13px;
    font-weight: 700;
    color: #374151;
}

/* 질량 → 환산 요약(히어로): 패널 최상단 · 가스별 표보다 위계 높음 */
.line-detail-gas-hero {
    margin-bottom: 0;
}

.line-detail-compare-bar {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px 10px;
    margin-bottom: 6px;
    padding: 6px 8px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
}

.line-detail-compare-bar--hero {
    margin-bottom: 0;
    padding: 12px 14px;
    background: #ffffff;
    border: 1px solid #d1fae5;
    border-left: 4px solid #52b788;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.line-detail-compare-bar--hero .line-detail-compare-v {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: -0.02em;
}

.line-detail-compare-bar--hero .line-detail-compare-group--emph .line-detail-compare-v {
    font-size: 19px;
    color: #14532d;
}

.line-detail-compare-bar--hero .line-detail-compare-k {
    font-weight: 700;
}

.line-detail-gas-detail-label {
    margin: 0 0 7px;
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
}

.line-gas-conversion-note {
    margin-top: 6px;
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #f8fafc;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 8px;
    line-height: 1.35;
}

.line-gas-conversion-note-label {
    font-size: 11px;
    font-weight: 700;
    color: #475569;
}

.line-gas-conversion-note-text {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
}

.line-detail-compare-group {
    display: inline-flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 6px;
    min-width: 0;
}

.line-detail-compare-group--emph .line-detail-compare-v {
    color: #166534;
    font-weight: 800;
}

.line-detail-compare-k {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
}

.line-detail-compare-v {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    font-variant-numeric: tabular-nums;
}

.line-detail-compare-u {
    font-size: 12px;
    font-weight: 600;
    color: #9ca3af;
}

.line-detail-compare-arrow {
    color: #d1d5db;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
}

/* 가스 표: 가스=열, 질량·CO2eq=행 */
.line-gas-horizontal-wrap {
    overflow-x: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
}

.line-gas-table--horizontal {
    width: 100%;
    min-width: 560px;
    border-collapse: collapse;
    font-size: 12px;
}

.line-gas-table--horizontal th,
.line-gas-table--horizontal td {
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    vertical-align: middle;
}

.line-gas-table--horizontal thead th {
    background: #f8fafc;
    font-weight: 700;
    color: #374151;
    text-align: center;
}

.line-gas-corner {
    position: sticky;
    left: 0;
    z-index: 2;
    min-width: 72px;
    text-align: left;
    background: #f8fafc;
    box-shadow: 1px 0 0 #e5e7eb;
}

.line-gas-col-gas {
    min-width: 56px;
    text-align: center;
}

.line-gas-col-total {
    min-width: 72px;
    font-weight: 700;
    color: #111827;
    background: #f9fafb;
    text-align: center;
}

.line-gas-row-label {
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: 72px;
    text-align: left;
    font-weight: 700;
    color: #4b5563;
    background: #f9fafb;
    box-shadow: 1px 0 0 #e5e7eb;
}

.line-gas-table--horizontal tbody th {
    border-right: 1px solid #e5e7eb;
}

.line-gas-num {
    font-variant-numeric: tabular-nums;
    color: #374151;
    text-align: left;
}

.line-gas-num--eq {
    color: #166534;
    font-weight: 600;
}

.line-gas-table--horizontal tbody td {
    text-align: center;
}

.line-gas-table--horizontal tbody td.text-right,
.line-gas-table--horizontal tbody td.line-gas-col-total {
    text-align: left;
}

/* 환산(CO2eq) 행 — 질량 행 대비 아주 약한 배경(스티키·합계 열 동일 톤) */
.line-gas-table--horizontal tbody tr.line-gas-row--co2eq > th,
.line-gas-table--horizontal tbody tr.line-gas-row--co2eq > td {
    background-color: #f4faf7;
}

/* 식별 정보: 5열 (기간 · 시설 · 카테고리 · 유형 · 활동) */
.line-detail-meta-compact {
    display: grid;
    gap: 8px;
}

.line-detail-meta-compact--five {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.line-detail-meta-cell {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 8px 10px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.line-detail-meta-cell--grow .line-detail-meta-v {
    white-space: normal;
    word-break: break-word;
}

.line-detail-meta-l {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    line-height: 1.25;
}

.line-detail-meta-v {
    font-size: 12px;
    font-weight: 700;
    color: #111827;
    line-height: 1.35;
    white-space: normal;
    word-break: break-word;
}

.line-detail-formula {
    margin-top: 0;
}

.line-detail-formula .formula-code-compact {
    padding: 8px 10px;
    font-size: 12px;
    line-height: 1.5;
}

/* 계산식 요약 카드 — 클릭 시 계산 상세 모달 */
.line-detail-calc-card {
    margin: 0;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    background: #ffffff;
    transition:
        border-color 0.15s ease,
        background 0.15s ease;
}

.line-detail-calc-card:hover {
    border-color: #9ca3af;
    background: #fcfcfd;
}

.line-detail-calc-card:focus {
    outline: none;
}

.line-detail-calc-card:focus-visible {
    box-shadow:
        0 0 0 2px #ffffff,
        0 0 0 4px #94a3b8;
}

.line-detail-calc-card:hover .line-detail-calc-card-chevron {
    color: #6b7280;
    transform: translateX(2px);
}

.line-detail-calc-card-body {
    padding: 12px;
    background: #ffffff;
    border-left: 2px solid #9ca3af;
}

.line-detail-calc-card-body--row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
}

.line-detail-calc-card-body--empty.line-detail-calc-card-body--row {
    align-items: center;
}

.line-detail-calc-card-body--empty .line-detail-calc-card-chevron {
    margin-top: 0;
}

.line-detail-calc-card-main {
    flex: 1;
    min-width: 0;
}

.line-detail-calc-card:hover .line-detail-calc-card-body {
    background: #f9fafb;
}

.line-detail-calc-code {
    display: block;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    word-break: break-word;
    white-space: pre-wrap;
}

.line-detail-calc-code.formula-code-compact {
    padding: 0;
    border: none;
    background: transparent;
    font-size: 12px;
    line-height: 1.5;
}

.line-detail-calc-card-body--empty {
    min-height: 48px;
}

.line-detail-calc-empty-text {
    font-size: 12px;
    color: #6b7280;
    flex: 1;
    min-width: 0;
}

.line-detail-calc-card-chevron {
    font-size: 22px;
    color: #d1d5db;
    flex-shrink: 0;
    margin-top: 1px;
    transition:
        color 0.15s ease,
        transform 0.15s ease;
}

.line-calc-json-modal-body {
    padding-top: 0;
}

.json-display--line-calc {
    max-height: min(65vh, 560px);
    margin: 0;
    padding: 12px;
    font-size: 12px;
}

/* 기본 정보 그리드 */
.detail-info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 12px;
    background: #f9fafb;
    border-radius: 6px;
}

.detail-info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.detail-info-item .detail-label {
    font-size: 10px;
    font-weight: 600;
    color: #666666;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.detail-info-item .detail-value {
    font-size: 12px;
    font-weight: 600;
    color: #1a1a1a;
}

/* 배출량 정보 강조 */
.emission-info-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    padding: 10px 12px;
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    border-radius: 6px;
    border: 1px solid #bbf7d0;
}

.emission-info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    justify-content: center;
}

.emission-info-item.highlight {
    background: #ffffff;
    padding: 8px 10px;
    border-radius: 6px;
    border: 2px solid #52b788;
}

.emission-label {
    font-size: 10px;
    font-weight: 600;
    color: #666666;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.emission-value {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
}

.emission-unit {
    font-size: 11px;
    font-weight: 600;
    color: #52b788;
    margin-left: 4px;
}

/* 계산식 섹션 */
.gas-breakdown-section {
    margin-top: 12px;
    padding: 10px 12px;
    background: #f0f9f4;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

/* .gas-breakdown-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px 16px;
    margin-top: 8px;
} */

.gas-breakdown-item {
    font-size: 12px;
}

.gas-breakdown-item .gas-label {
    font-weight: 600;
    color: #495057;
    margin-right: 4px;
}

.gas-breakdown-item .gas-value {
    color: #2d8659;
}

.formula-section {
    padding: 0px 12px;
    background: #fafafa;
    border-left: 3px solid #52b788;
}

.formula-label {
    font-size: 11px;
    font-weight: 700;
    color: #666666;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.formula-code-compact {
    display: block;
    padding: 10px 12px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    font-size: 11px;
    line-height: 1.6;
    color: #1a1a1a;
    word-break: break-all;
}

/* 계산 상세 섹션 (접을 수 있게) */
.calculation-details-section {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
}

.calculation-details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #f9fafb;
    cursor: pointer;
    transition: background 0.2s ease;
    user-select: none;
}

.calculation-details-header:hover {
    background: #f3f4f6;
}

.calculation-details-title {
    font-size: 12px;
    font-weight: 700;
    color: #1a1a1a;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.calculation-details-header .material-icons {
    font-size: 20px;
    color: #666666;
    transition: transform 0.2s ease;
}

.calculation-details-content {
    background: #ffffff;
    border-top: 1px solid #e5e7eb;
}

.json-display {
    margin: 0;
    padding: 16px;
    background: #1e1e1e;
    color: #d4d4d4;
    font-size: 11px;
    line-height: 1.6;
    overflow-x: auto;
    font-family: "Consolas", "Monaco", "Courier New", monospace;
    white-space: pre;
    word-wrap: normal;
    max-height: 500px;
    overflow-y: auto;
}

.calculation-detail-item-compact {
    margin-bottom: 16px;
    padding: 12px;
    background: #f9fafb;
    border-radius: 6px;
    border-left: 3px solid #52b788;
}

.calculation-detail-item-compact:last-child {
    margin-bottom: 0;
}

.calculation-detail-item-compact strong {
    display: block;
    margin-bottom: 8px;
    font-size: 11px;
    font-weight: 700;
    color: #1a1a1a;
}

.components-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-top: 8px;
}

.component-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 6px 8px;
    background: #ffffff;
    border-radius: 4px;
}

.component-label {
    font-size: 10px;
    font-weight: 600;
    color: #666666;
}

.component-value {
    font-size: 11px;
    font-weight: 600;
    color: #1a1a1a;
}

.steps-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
}

.step-item {
    display: flex;
    gap: 12px;
    padding: 10px;
    background: #ffffff;
    border-radius: 4px;
}

.step-number {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #52b788;
    color: #ffffff;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 700;
}

.step-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.step-remark {
    font-size: 11px;
    font-weight: 600;
    color: #1a1a1a;
}

.step-formula {
    font-size: 12px;
    color: #666666;
    padding: 4px 8px;
    background: #f9fafb;
    border-radius: 3px;
}

.step-result {
    font-size: 11px;
    font-weight: 600;
    color: #52b788;
}

.sample-calculation-compact {
    margin-top: 8px;
    padding: 10px;
    background: #f9fafb;
    border-radius: 4px;
}

.sample-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
    font-size: 11px;
}

.sample-item:last-of-type {
    margin-bottom: 0;
}

.sample-label {
    font-weight: 600;
    color: #666666;
}

.sample-value {
    font-weight: 700;
    color: #1a1a1a;
}

.sample-formula-compact {
    margin-top: 8px;
    padding: 8px 10px;
    background: #ffffff;
    border-radius: 3px;
    font-size: 10px;
    color: #1a1a1a;
    font-weight: 600;
    border: 1px solid #e5e7eb;
}

.scope-badge {
    display: inline-block;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 700;
    border-radius: 3px;
    text-transform: uppercase;
}

.scope-badge.scope1 {
    color: #2c5234;
    background: #e8f5e9;
}

.scope-badge.scope2 {
    color: #1b4d3e;
    background: #d7f2e9;
}

.scope-badge.scope3 {
    color: #0d3d29;
    background: #c8eddd;
}

.text-left {
    text-align: left;
}

.text-center {
    text-align: center;
}

.text-right {
    text-align: right;
}

.emission-cell {
    font-weight: 700;
    color: #1a1a1a;
}

.item-value {
    font-weight: 700;
    color: #1a1a1a;
}

.item-percent {
    font-weight: 600;
    color: #666666;
}

/* 상세 모달 */
.detail-modal-content {
    padding: 8px 0;
}

/* 품질점검·불확도 가이드 모달 */
.guide-modal-content {
    max-height: 70vh;
    overflow-y: auto;
    padding: 8px 0;
}

.guide-modal-content :deep(.markdown-body) {
    font-size: 11px;
    line-height: 1.6;
}

.guide-modal-body {
    padding: 1rem 1.25rem;
}

.detail-modal-content .detail-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.detail-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
}

.detail-row:last-child {
    border-bottom: none;
}

.detail-label {
    font-size: 12px;
    font-weight: 700;
    color: #666666;
    min-width: 100px;
}

.detail-value {
    font-size: 12px;
    font-weight: 600;
    color: #1a1a1a;
}

.formula-row {
    align-items: flex-start;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
}

.detail-formula {
    flex: 1;
}

.formula-code {
    display: block;
    padding: 10px;
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.5;
    color: #1a1a1a;
    word-break: break-all;
}

.calculation-detail-item {
    margin-bottom: 16px;
    padding: 12px;
    background: #f9fafb;
    border-radius: 6px;
    border-left: 3px solid #52b788;
}

.calculation-detail-item strong {
    display: block;
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 700;
    color: #1a1a1a;
}

.calculation-components {
    margin: 8px 0 0 0;
    padding-left: 20px;
    list-style-type: disc;
}

.calculation-components li {
    margin-bottom: 4px;
    font-size: 11px;
    color: #666666;
}

.calculation-steps {
    margin: 8px 0 0 0;
    padding-left: 20px;
    list-style-type: decimal;
}

.calculation-steps li {
    margin-bottom: 12px;
    font-size: 11px;
    color: #666666;
}

.step-remark {
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 4px;
}

.step-result {
    font-weight: 600;
    color: #52b788;
    margin-top: 4px;
}

.sample-calculation {
    margin-top: 8px;
    padding: 8px;
    background: #ffffff;
    border-radius: 4px;
}

.sample-calculation > div {
    margin-bottom: 6px;
    font-size: 11px;
    color: #666666;
}

.sample-formula {
    margin-top: 8px;
    padding: 6px 10px;
    background: #f5f5f5;
    border-radius: 3px;
    font-size: 10px;
    color: #1a1a1a;
    font-weight: 600;
}

/* 반응형 */
@media (max-width: 1400px) {
    .line-detail-grid--summary,
    .line-detail-grid--factor,
    .line-detail-grid--bottom {
        grid-template-columns: 1fr;
    }

    .detail-info-grid {
        grid-template-columns: 1fr;
    }

    .line-detail-meta-compact--five {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .emission-info-section {
        grid-template-columns: 1fr;
    }

    .components-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 1024px) {
    .line-detail-modal-header {
        padding: 8px 12px;
    }

    .line-detail-modal-scroll {
        padding: 10px 8px 10px 10px;
    }

    :deep(.line-detail-bv-modal-footer) {
        padding: 8px 10px;
    }

    .top-metrics {
        grid-template-columns: 1fr;
    }

    .inv-panel-row {
        grid-template-columns: 1fr;
    }

    .line-detail-summary-stats {
        grid-template-columns: 1fr;
        min-width: 0;
        width: 100%;
    }

    .line-detail-meta-compact--five {
        grid-template-columns: 1fr;
    }
}
</style>
